import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import SkeletonWrapper from '../../../containers/NewProductList/components/SkeletonWrapper/SkeletonWrapper'
import DisplayRecentPolicies from './DisplayRecentPolicies'
import SambaPolicyFilters, {
  buildSambaFilterParams,
  defaultSambaInputs
} from './SambaPolicyFilters'
import SambaRecentPolicies from './SambaRecentPolicies'
import { useStyle } from './Style'
import { hasAuthourity, getUserBranch } from '../../../lib/access.es6'

function TabPanel(props) {
  const { children, value, index } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  }
}

// 'Un Confirmed' uses the excludeFirstStep filter (see makerCheckStage.js):
// it shows a policy through Sales Approval and Quality Check, then drops it
// once it reaches its AML stage, which has its own dedicated queue. Restored
// 2026-08-26 alongside PolicyHeader.js's MAKER_CHECKS_DISABLED_PARTNERS.
// Cool Off defaults to start_date in the date filter since its natural query
// is "policies whose coverage starts next month" (paid this month, still in
// cool-off window). The user can override via the filter bar.
export const OMM_TABS = [
  {
    label: 'Un Confirmed',
    status: 'Un_Confirmed',
    needAction: 'exclude',
    makerStage: 'excludeFirstStep'
  },
  {
    label: 'Need Action',
    status: 'Un_Confirmed',
    needAction: 'only',
    emptyMessage: 'No policies need action.'
  },
  { label: 'Un Paid', status: 'Unpaid' },
  {
    label: 'Cool Off',
    status: 'Full_Coverage',
    dateDiscriminator: 'start_date'
  },
  {
    label: 'Rejected Policies',
    status: 'Rejected',
    emptyMessage: 'No rejected policies found.'
  }
]

// Every OMM user sees every bucket (Un Confirmed, Need Action, Un Paid, Cool
// Off, Rejected Policies) regardless of role. Every place that reads OMM_TABS
// for rendering or for the initial/default tab goes through this so there is
// a single place to change if that ever needs to differ by role again.
export function getVisibleOmmTabs() {
  return OMM_TABS
}

// Build base API filter params for an OMM tab: status and optional branch
// scope for agents/managers. No date range — the user applies that via the
// filter bar above each tab's table.
export function buildOmmTabParams(tab) {
  const params = { status: tab.status }
  if (hasAuthourity('OMM_SALES_AGENT') || hasAuthourity('OMM_SALES_MANAGERS')) {
    const branch = getUserBranch()
    if (branch && branch !== 'N/A') {
      params.additional_details = { agent_branch: branch }
    }
  }
  return params
}

const RecentPoliciesWrapper = props => {
  const classes = useStyle()

  // tabValue/tabParams serve the OMM path and sambaFilters the samba path,
  // but hooks must be called unconditionally (Rules of Hooks).
  const [tabValue, setTabValue] = useState(0)
  // tabParams carries the current tab's filter so pagination within a tab
  // inherits the same status, date range and date_discriminator.
  const [tabParams, setTabParams] = useState(function() {
    return buildOmmTabParams(getVisibleOmmTabs()[0])
  })
  // sambaFilters carries the applied filter params so pagination inherits
  // the same status, date range and beneficiary filter. It starts on the
  // same default view the container's initial fetch uses (start_date in
  // the last 30 days).
  const [sambaFilters, setSambaFilters] = useState(function() {
    return buildSambaFilterParams(defaultSambaInputs())
  })
  // searchTerm is shared across all paths; reset on filter/tab change.
  const [searchTerm, setSearchTerm] = useState('')

  // Samba-only: filter panel + policies table. Rendered ahead of the loader
  // early-return so the filter inputs keep their state while a page loads.
  if (props.isSamba) {
    const applySambaFilters = function(params) {
      setSambaFilters(params)
      setSearchTerm('')
      props.getPolicies({ page: 1, size: 20, ...params })
    }

    // Merge pagination calls from the table (which only supply page/size)
    // with the currently applied filters and active search term.
    const getPoliciesWithFilters = function(payload) {
      props.getPolicies({
        ...sambaFilters,
        ...(searchTerm ? { policy_number: searchTerm } : {}),
        ...payload
      })
    }

    const handleSearch = function(value) {
      setSearchTerm(value)
      props.getPolicies({
        ...sambaFilters,
        page: 1,
        size: 20,
        ...(value ? { policy_number: value } : {})
      })
    }

    return (
      <section className={classes.claimswrapper}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <SambaPolicyFilters
              onApply={applySambaFilters}
              disabled={props.loader}
              searchValue={searchTerm}
              onSearch={handleSearch}
            />
            {props.loader ? (
              <SkeletonWrapper loading={props.loader} />
            ) : (
              <SambaRecentPolicies
                policies={props.policies}
                handleNextPage={getPoliciesWithFilters}
                loader={props.loader}
                page={props.page}
                size={props.size}
                total={props.total}
              />
            )}
          </Grid>
        </Grid>
      </section>
    )
  }

  if (props.loader) {
    return <SkeletonWrapper loading={props.loader} />
  }

  // OMM-only: 3-tab "New Business" layout
  if (props.isOmm) {
    const handleTabChange = function(_event, newValue) {
      setTabValue(newValue)
      setSearchTerm('')

      const tab = getVisibleOmmTabs()[newValue]
      const params = buildOmmTabParams(tab)
      setTabParams(params)
      props.getPolicies({
        page: 1,
        size: 20,
        ...(tab.needAction ? { fetchAll: true } : {}),
        ...params
      })
    }

    // Wrap getPolicies so that pagination calls from DisplayRecentPolicies
    // (which only supply page/size) are automatically merged with the current
    // tab's status, date-range params, and active search term.
    const getPoliciesWithTabContext = function(payload) {
      props.getPolicies({
        ...tabParams,
        ...(searchTerm ? { policy_number: searchTerm } : {}),
        ...payload
      })
    }

    const handleSearch = function(value) {
      setSearchTerm(value)
      const tab = getVisibleOmmTabs()[tabValue]
      props.getPolicies({
        ...tabParams,
        page: 1,
        size: 20,
        ...(tab.needAction ? { fetchAll: true } : {}),
        ...(value ? { policy_number: value } : {})
      })
    }

    return (
      <section className={classes.claimswrapper}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="New Business policy tabs"
                variant="scrollable"
                scrollButtons="auto"
                className={classes.tabs}
              >
                {getVisibleOmmTabs().map((tab, idx) => (
                  <Tab key={tab.label} label={tab.label} {...a11yProps(idx)} />
                ))}
              </Tabs>
            </Box>

            {getVisibleOmmTabs().map((tab, idx) => (
              <TabPanel key={tab.label} value={tabValue} index={idx}>
                <DisplayRecentPolicies
                  policies={props.policies}
                  handleNextPage={getPoliciesWithTabContext}
                  loader={props.loader}
                  page={props.page}
                  size={props.size}
                  total={props.total}
                  needAction={tab.needAction}
                  makerStage={tab.makerStage}
                  emptyMessage={tab.emptyMessage}
                  showAmlFlag
                  searchValue={searchTerm}
                  onSearch={handleSearch}
                />
              </TabPanel>
            ))}
          </Grid>
        </Grid>
      </section>
    )
  }

  // Non-OMM: original single-panel layout
  const getPoliciesWithSearch = function(payload) {
    props.getPolicies({
      ...(searchTerm ? { policy_number: searchTerm } : {}),
      ...payload
    })
  }

  const handleSearch = function(value) {
    setSearchTerm(value)
    props.getPolicies({
      page: 1,
      size: 20,
      ...(value ? { policy_number: value } : {})
    })
  }

  return (
    <>
      <section className={classes.claimswrapper}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <TabPanel value={0} index={0}>
              <DisplayRecentPolicies
                policies={props.policies}
                handleNextPage={getPoliciesWithSearch}
                loader={props.loader}
                page={props.page}
                size={props.size}
                total={props.total}
                searchValue={searchTerm}
                onSearch={handleSearch}
              />
            </TabPanel>
          </Grid>
        </Grid>
      </section>
    </>
  )
}

export default RecentPoliciesWrapper
